import React from 'react'
import { useAuth, useIsOffline } from '@oneblink/apps-react'
import { formElementsService } from '@oneblink/sdk-core'

import makeIsShowing from '../../../lib/is-element-showing'
import {
  findMeasures,
  processMeasures,
  getVisibleSubCarriers,
  getMeasuresRequirementsHTML,
} from '../../../lib/measures-and-requirements'
import { findPage } from 'lib/definition-manipulations'
import { useReferenceData } from '../../../Providers/ReferenceDataProvider'

export default function useUpdateSubmission() {
  const isOffline = useIsOffline()
  const { referenceData } = useReferenceData()
  const { userProfile } = useAuth()

  return React.useCallback(
    (submission, definition) => {
      // make the carrier checkboxes work more like a radio
      if (
        Array.isArray(submission.Cattle_Tick_Carrier) &&
        submission.Cattle_Tick_Carrier.length
      ) {
        // take the last selected element as the only selected option
        submission.Cattle_Tick_Carrier = [
          submission.Cattle_Tick_Carrier[
            submission.Cattle_Tick_Carrier.length - 1
          ],
        ]

        // horse, donkey, mule
        if (
          submission.Cattle_Tick_Carrier.includes('Equine') &&
          Array.isArray(submission.Cattle_Tick_Carrier_Sub) &&
          submission.Cattle_Tick_Carrier_Sub.length
        ) {
          const lastSelection =
            submission.Cattle_Tick_Carrier_Sub[
              submission.Cattle_Tick_Carrier_Sub.length - 1
            ]
          if (lastSelection === 'Horse') {
            submission.Cattle_Tick_Carrier_Sub =
              submission.Cattle_Tick_Carrier_Sub.filter(
                (species: string) => species === 'Horse',
              )
          } else {
            const donkeyAndMule = submission.Cattle_Tick_Carrier_Sub.filter(
              (species: string) => species === 'Donkey' || species === 'Mule',
            )
            // if the last selection was donkey or mule, remove other selections
            if (donkeyAndMule.includes(lastSelection)) {
              submission.Cattle_Tick_Carrier_Sub = donkeyAndMule
            }
          }
        }
      }

      let destinationPrefilled = false

      const measuresData = referenceData?.find(
        (d) => d.type === 'requirementsAndMeasures',
      )

      if (measuresData && measuresData.type === 'requirementsAndMeasures') {
        const measures = findMeasures(
          submission,
          measuresData.value,
          definition,
        )
        console.log('found measures: ', measures)
        const selectedAnimals = (submission.Cattle_Tick_Carrier || []).concat(
          getVisibleSubCarriers(submission, definition),
        )

        const {
          mandatoryMeasures,
          preliminaryRequirements,
          finalRequirements,
        } = processMeasures(measures, selectedAnimals)

        const mandatoryMeasuresStr = mandatoryMeasures
          .map((m) => m.value)
          .join(',')

        const finalRequirementsStr = finalRequirements
          .map((m) => m.value)
          .join(',')

        const haveMandatoryMeasuresChanged =
          submission.Mandatory_Measures !== mandatoryMeasuresStr

        const haveFinalRequirementsChanged =
          submission.Final_Requirement !== finalRequirementsStr

        if (haveMandatoryMeasuresChanged || haveFinalRequirementsChanged) {
          submission.DeclarationMandatoryMeasures = undefined
        }

        submission.Mandatory_Measures = mandatoryMeasuresStr
        submission.Final_Requirement = finalRequirementsStr

        submission.Final_Requirement_HTML =
          getMeasuresRequirementsHTML(finalRequirements)

        submission.Mandatory_Measures_HTML =
          getMeasuresRequirementsHTML(mandatoryMeasures)

        submission.Preliminary_Requirement = preliminaryRequirements
          .map((m) => m.value)
          .join(',')

        submission.Preliminary_Requirement_HTML = getMeasuresRequirementsHTML(
          preliminaryRequirements,
        )

        submission.isBioCertRequired__hidden = measures.some(
          (measure) => measure.Bio_Cert_Required,
        )

        submission.hasDippingRequirement__hidden = measures.some((measure) => {
          return measure.Treatment_Option === 'Dipping only'
        })

        submission.isTreatmentVarious__hidden = measures.some((measure) => {
          return measure.Treatment_Option === 'Various including dipping'
        })
      }

      //---- all submission modifications affecting isShowing above this line  -----//
      const isShowing = makeIsShowing(submission, definition)

      const validDestiPic =
        isShowing('valid_Desti_Pic') && submission.valid_Desti_Pic === 'Yes'
      submission.show_Destination_PIC_Offline__hidden =
        destinationPrefilled ||
        (isOffline && validDestiPic) ||
        submission.destinationPicUnavailable__hidden

      submission.isOffline__exclude__hidden = isOffline

      let canUserClickNext = true
      const firstPage = findPage(definition, 'Movement overview')
      if (firstPage) {
        formElementsService.forEachFormElement(firstPage.elements, (el) => {
          if (!canUserClickNext) {
            //if it's already false don't bother do the below
            return
          }
          try {
            if ('name' in el && el.meta) {
              const meta = JSON.parse(el.meta)
              if ('canClickNext' in meta) {
                // to continue with the form either the blocking element is not showing
                // or canClickNext is not false
                if (isShowing(el.name) && meta.canClickNext === false) {
                  console.log(`${el.name} causing declaration to be invisible`)
                  canUserClickNext = false
                }
              }
            }
          } catch (e) {
            //do nothing
          }
        })
      }
      submission.canUserClickNextOnFirstPage = canUserClickNext

      const validOriPic =
        isShowing('valid_Ori_Pic') && submission.valid_Ori_Pic === 'Yes'
      submission.show_Origin_PIC_Offline__hidden =
        (isOffline && validOriPic) || submission.originPicUnavailable__hidden

      const loggedInUser = `${userProfile?.firstName || ''} ${
        userProfile?.lastName || ''
      }`
      const stockOwnerFullName = `${submission.Stock_Owner_First_Name} ${submission.Stock_Owner_Last_Name}`
      const whoIsDeclaringIsVisible = isShowing('whoIsDeclaring')
      const isStockOwnerLoggedIn =
        submission.Origin_Stock_Owner === 'Yes' &&
        !submission.isDPIOfficer__exclude__hidden

      const isStockOwnerDeclaring =
        whoIsDeclaringIsVisible &&
        submission.whoIsDeclaring === stockOwnerFullName

      const isDPIOfficerAssisting =
        submission.DPI_Authorised_officer_Submission ===
        'I am assisting the client to submit a digital ROM'

      const isLoggedInUserDeclaring =
        whoIsDeclaringIsVisible && submission.whoIsDeclaring === loggedInUser

      if (isDPIOfficerAssisting || isStockOwnerDeclaring) {
        submission.Declaration_First_Name = submission.Stock_Owner_First_Name
        submission.Declaration_Last_Name = submission.Stock_Owner_Last_Name
        submission.Declaration_Email_address = submission.Stock_Owner_Email
      } else if (isLoggedInUserDeclaring || isStockOwnerLoggedIn) {
        submission.Declaration_First_Name = submission.firstName
        submission.Declaration_Last_Name = submission.lastName
        submission.Declaration_Email_address = submission.email
      } else {
        submission.Declaration_First_Name = undefined
        submission.Declaration_Last_Name = undefined
        submission.Declaration_Email_address = undefined
      }

      submission.isStockOwnerDeclaring = isStockOwnerDeclaring

      const noPicReasonsData = referenceData?.find(
        (ref) => ref.type === 'noPICReasons',
      )

      if (noPicReasonsData?.type === 'noPICReasons') {
        const otherValue = noPicReasonsData.value.find(
          (noPicReason) => noPicReason.No_PIC_Reason === 'Other',
        )?.CTROM_No_PIC_Reason_Id

        submission.noOriginPicOtherSelected__hidden =
          submission.reason_No_Origin_Pic === otherValue

        submission.noDestinationPicOtherSelected__hidden =
          submission.reason_No_Destination_Pic === otherValue
      }

      const vetDecSubform = submission.vetDeclaration as Record<string, string>
      submission.vetDeclaration = {
        ...vetDecSubform,
        vetIsPresent__hidden: submission.vetPresent === 'present',
      }

      if (
        submission.isDPIOfficer__exclude__hidden &&
        submission.Origin_Stock_Owner !== 'No'
      ) {
        submission.Origin_Stock_Owner = 'No'
      }

      submission.hasCattleBuffaloBisonDeer = [
        'Cattle',
        'Bison',
        'Buffalo',
        'Deer',
      ].some((animal) =>
        (submission.Cattle_Tick_Carrier ?? []).includes(animal),
      )

      return submission
    },
    [referenceData, isOffline, userProfile?.firstName, userProfile?.lastName],
  )
}
